import { ExternalLinkIcon } from "@heroicons/react/outline";
import { CheckCircleIcon } from "@heroicons/react/solid";
import { ChevronRight } from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";

import { SidebarFavoriteStar } from "./SidebarFavoriteStar";

import { convertActionsToContextMenuItems } from "@/components/ui/Cards/Card/utils";
import { ContextMenu } from "@/components/ui/ContextMenu";
import { ContextMenuItems } from "@/components/ui/ContextMenu/types";
import { Action, DropdownMenuVariant } from "@/components/ui/DropdownMenu";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import DraggableItem from "@/components/UnifiedRegistry/DragAndDrop/DraggableItem";
import { useContextualActions } from "@/components/UnifiedRegistry/hooks/useContextualActions";
import { useItemSelection } from "@/components/UnifiedRegistry/hooks/useItemSelection";
import { useSmartTableNavigationPrefetch } from "@/components/UnifiedRegistry/hooks/useSmartTableNavigationPrefetch";
import { RegistryItem } from "@/components/UnifiedRegistry/types";
import { useUnifiedRegistry } from "@/components/UnifiedRegistry/unified-registry-context";
import { getItemLink } from "@/components/UnifiedRegistry/utils";
import { useUser } from "@/context/user-context";
import { cn } from "@/lib/utils";
import { Entity, EntityType, getEntityNumericId } from "@/queries/folder-entities/types";

interface TreeItemProps {
  item: Entity;
  depth?: number;
  isActive: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  onToggleFolder: (folderId: number) => void;
  onNavigate: (item: Entity) => void;
  getItemIcon: (item: Entity, isCollapsed?: boolean) => React.ReactNode;
}

export const TreeItem: React.FC<TreeItemProps> = observer(
  ({
    item,
    depth = 0,
    isActive,
    isExpanded,
    hasChildren,
    onToggleFolder,
    onNavigate,
    getItemIcon,
  }) => {
    const isFolder = item.type === EntityType.FOLDER;
    const workspaceId = useUser().activeWorkspaceId!;
    const registry = useUnifiedRegistry();
    const { handleItemClick: handleSelectionClick } = useItemSelection();
    const [isHovered, setIsHovered] = useState(false);

    const registryItem = item as RegistryItem;
    const isSelected = registry.isSelected(registryItem);

    const { contextMenuActions } = useContextualActions(item);
    const link = getItemLink(item, workspaceId);
    const prefetchSmartTableNavigation = useSmartTableNavigationPrefetch({
      item: registryItem,
      workspaceId,
    });

    const contextMenuItems: ContextMenuItems<Entity>[] = useMemo(() => {
      const baseActions: Action<Entity>[] = [
        {
          id: "open-in-new-tab",
          label: "Open in new tab",
          icon: ExternalLinkIcon,
          onClick: () => window.open(link, "_blank"),
          variant: DropdownMenuVariant.PRIMARY,
        },
      ];

      if (contextMenuActions && contextMenuActions.length > 0) {
        baseActions.push(
          { id: "divider", isDivider: true } as Action<Entity>,
          ...(contextMenuActions as Action<Entity>[]),
        );
      }

      return convertActionsToContextMenuItems(baseActions) as ContextMenuItems<Entity>[];
    }, [contextMenuActions, link]);

    const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      const folderId = getEntityNumericId(item.id);
      if (folderId !== undefined) {
        onToggleFolder(folderId);
      }
    };

    const handleClick = (e: React.MouseEvent) => {
      const handled = handleSelectionClick(e, registryItem);
      if (handled) {
        return;
      }
      e.preventDefault();
      prefetchSmartTableNavigation();
      onNavigate(item);
    };

    return (
      <DraggableItem item={registryItem} className="h-full">
        <ContextMenu contextMenuItems={contextMenuItems} data={item} modal={false}>
          <div
            className={cn(
              "group/treeItem relative flex cursor-pointer select-none items-center rounded-md px-2 py-1 text-sm transition-all duration-200",
              isActive
                ? "bg-gray-100 font-medium text-gray-900"
                : "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
              isSelected && "bg-blue-50 text-gray-900 hover:bg-blue-100",
            )}
            style={{
              paddingLeft: `${8 + depth * 18}px`,
            }}
            onClick={handleClick}
            onPointerEnter={prefetchSmartTableNavigation}
            onFocus={prefetchSmartTableNavigation}
            onMouseDown={prefetchSmartTableNavigation}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="relative min-w-0 flex-1 overflow-hidden">
              {isFolder && (
                <span
                  role="presentation"
                  aria-hidden="true"
                  className={cn(
                    "pointer-events-none absolute left-0 z-10 flex h-4 w-4 items-center justify-center",
                    "opacity-0 transition-opacity duration-150",
                    "group-hover/treeItem:pointer-events-auto group-hover/treeItem:opacity-100",
                    "group-focus-within/treeItem:pointer-events-auto group-focus-within/treeItem:opacity-100",
                  )}
                  onClick={handleToggle}
                  onPointerDown={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                >
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 text-black transition-transform duration-200",
                      isExpanded && "rotate-90",
                    )}
                    strokeWidth={2}
                  />
                </span>
              )}
              <div
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-1.5",
                  isFolder &&
                    "transition-transform duration-150 group-focus-within/treeItem:translate-x-[18px] group-hover/treeItem:translate-x-[18px]",
                )}
              >
                {isSelected && (
                  <CheckCircleIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                )}
                {getItemIcon(item, !isExpanded || !hasChildren)}
                <OverflowDisplayTooltip
                  content={item.name}
                  className={cn("min-w-0 flex-1 text-[13px]", isActive && "font-semibold")}
                  side="right"
                  delayDuration={500}
                >
                  {item.name}
                </OverflowDisplayTooltip>
              </div>
            </div>
            <SidebarFavoriteStar entityType={item.type} entityId={item.id} isVisible={isHovered} />
          </div>
        </ContextMenu>
      </DraggableItem>
    );
  },
);

TreeItem.displayName = "TreeItem";
